import { useState } from 'react';
import {
  createInvoiceSchema,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
} from '@faktorplus/shared';
import { useCreateInvoice, useInvoiceList } from '../../api/invoices';
import { useVendors } from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { Field, Input, Select, Textarea } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { ErrorBox } from '../../components/ui/feedback';
import { ApiError, uploadAttachment } from '../../lib/api-client';
import { formatMoney } from '../../lib/format';
import {
  LineItemsEditor,
  sumLineItems,
  toLineItemInputs,
  type DraftLineItem,
} from './line-items-editor';

const DEFAULT_VAT_RATE = 0.23;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Manual entry for the invoices that never come through KSeF: the handful of
 * small suppliers below the threshold, and fuel receipts from the till.
 *
 * The office types the net amount and the form derives VAT and gross, because
 * a mismatch between the three is the one thing the API refuses outright.
 */
export function CreateInvoiceDialog({ onClose }: { onClose: () => void }) {
  const { data: vendors = [] } = useVendors();
  const create = useCreateInvoice();

  const [vendorId, setVendorId] = useState('');
  // Correction mode (24.09.2026): the amounts become the signed delta and the
  // document points at the invoice it corrects.
  const [isCorrection, setIsCorrection] = useState(false);
  const [correctsInvoiceId, setCorrectsInvoiceId] = useState('');
  const [number, setNumber] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('TRANSFER');
  // Suggested as today, because that is when the receipt usually reaches the
  // office; cleared by the accountant when the real date is unknown.
  const [paidAt, setPaidAt] = useState(today());
  const [net, setNet] = useState('');
  const [vat, setVat] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<DraftLineItem[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // The corrected invoice is picked from the vendor's live documents; another
  // correction cannot be corrected, so those drop out of the list.
  const { data: vendorInvoices } = useInvoiceList(
    { vendorId, pageSize: 200, sortBy: 'issueDate', sortDir: 'desc' },
    { enabled: isCorrection && vendorId !== '' },
  );
  const correctableInvoices = (vendorInvoices?.items ?? []).filter(
    (invoice) => invoice.correctsInvoiceId === null,
  );

  // Once positions are entered they are the source of the total; the amount
  // fields stop being editable rather than silently disagreeing with them.
  const hasItems = !isCorrection && items.length > 0;
  const itemTotals = sumLineItems(items);

  const netAmount = hasItems ? itemTotals.net : Number(net) || 0;
  // Blank VAT means "standard rate" — the office overrides it for the odd 8% or 0%.
  const vatAmount = hasItems
    ? itemTotals.vat
    : vat === ''
      ? round(netAmount * DEFAULT_VAT_RATE)
      : Number(vat) || 0;
  const grossAmount = round(netAmount + vatAmount);

  async function handleSubmit() {
    setValidationError(null);

    if (isCorrection && !correctsInvoiceId) {
      setValidationError('Wskaż fakturę, której dotyczy korekta');
      return;
    }

    const parsed = createInvoiceSchema.safeParse({
      number: number.trim(),
      vendorId,
      issueDate: new Date(issueDate),
      // A correction is not a liability: no term, no cash, no positions.
      dueDate: isCorrection || !dueDate ? null : new Date(dueDate),
      paymentMethod: isCorrection ? 'TRANSFER' : paymentMethod,
      paidAt: !isCorrection && paymentMethod === 'CASH' && paidAt ? new Date(paidAt) : null,
      netAmount,
      vatAmount,
      grossAmount,
      notes: notes.trim() || null,
      lineItems: isCorrection ? [] : toLineItemInputs(items),
      correctsInvoiceId: isCorrection ? correctsInvoiceId : null,
    });

    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Sprawdź wprowadzone dane');
      return;
    }

    const invoice = await create.mutateAsync(parsed.data);

    // The scan is a second request; a failed upload must not lose the invoice
    // that was already created, so it reports separately.
    if (file) {
      setIsUploading(true);
      try {
        await uploadAttachment(invoice.id, file);
      } catch (error) {
        setIsUploading(false);
        setValidationError(
          `Faktura została zapisana, ale nie udało się wgrać skanu: ${
            error instanceof ApiError ? error.message : 'błąd połączenia'
          }. Dodaj go z poziomu faktury.`,
        );
        return;
      }
      setIsUploading(false);
    }

    onClose();
  }

  const isPending = create.isPending || isUploading;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isCorrection ? 'Nowa faktura korygująca' : 'Nowa faktura'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Anuluj
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button
            variant="primary"
            isLoading={isPending}
            disabled={!vendorId}
            onClick={() => void handleSubmit()}
          >
            {isCorrection ? 'Dodaj korektę' : 'Dodaj fakturę'}
          </Button>
        </>
      }
    >
      <Field label="Kontrahent">
        {(id) => (
          <Select id={id} value={vendorId} onChange={(event) => setVendorId(event.target.value)}>
            <option value="">— wybierz kontrahenta</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Rodzaj dokumentu">
        {(id) => (
          <Select
            id={id}
            value={isCorrection ? 'CORRECTION' : 'INVOICE'}
            onChange={(event) => setIsCorrection(event.target.value === 'CORRECTION')}
          >
            <option value="INVOICE">Faktura</option>
            <option value="CORRECTION">Faktura korygująca</option>
          </Select>
        )}
      </Field>

      {isCorrection && (
        <Field
          label="Faktura korygowana"
          hint={
            vendorId
              ? 'Wpisz różnicę względem faktury pierwotnej. Minus oznacza, że kontrahent ma nam oddać.'
              : 'Najpierw wybierz kontrahenta.'
          }
        >
          {(id) => (
            <Select
              id={id}
              value={correctsInvoiceId}
              disabled={!vendorId}
              onChange={(event) => setCorrectsInvoiceId(event.target.value)}
            >
              <option value="">— wybierz fakturę</option>
              {correctableInvoices.map((invoice) => (
                <option key={invoice.id} value={invoice.id}>
                  {invoice.number} · {formatMoney(invoice.grossAmount)}
                  {invoice.paidAt ? ' · zapłacona' : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      <Field label={isCorrection ? 'Numer faktury korygującej' : 'Numer faktury'}>
        {(id) => (
          <Input
            id={id}
            value={number}
            placeholder="FS/548/KK0/08/2026"
            onChange={(event) => setNumber(event.target.value)}
          />
        )}
      </Field>

      <div className={modalStyles.row}>
        <Field label="Data wystawienia">
          {(id) => (
            <Input
              id={id}
              type="date"
              value={issueDate}
              onChange={(event) => setIssueDate(event.target.value)}
            />
          )}
        </Field>

        {!isCorrection && (
          <Field label="Termin płatności">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={dueDate}
                placeholder="opcjonalnie"
                onChange={(event) => setDueDate(event.target.value)}
              />
            )}
          </Field>
        )}
      </div>

      {!isCorrection && (
      <div className={modalStyles.row}>
        <Field
          label="Forma płatności"
          hint={
            paymentMethod === 'CASH'
              ? 'Zapłacona przy kasie — nie trafi do rozrachunków.'
              : undefined
          }
        >
          {(id) => (
            <Select
              id={id}
              value={paymentMethod}
              onChange={(event) => setPaymentMethod(event.target.value as PaymentMethod)}
            >
              {PAYMENT_METHODS.map((method) => (
                <option key={method} value={method}>
                  {PAYMENT_METHOD_LABELS[method]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {paymentMethod === 'CASH' && (
          <Field label="Data zapłaty" hint="Zostaw puste, jeśli nie znasz daty. Uzupełnisz ją później.">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={paidAt}
                onChange={(event) => setPaidAt(event.target.value)}
              />
            )}
          </Field>
        )}
      </div>
      )}

      <div className={modalStyles.row}>
        <Field label={isCorrection ? 'Różnica netto (±)' : 'Kwota netto'}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={isCorrection ? undefined : '0'}
              step="0.01"
              value={hasItems ? netAmount.toFixed(2) : net}
              readOnly={hasItems}
              placeholder={isCorrection ? 'np. -10000' : undefined}
              onChange={(event) => setNet(event.target.value)}
            />
          )}
        </Field>

        <Field label="VAT" hint={!hasItems && vat === '' ? 'Puste = 23%' : undefined}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={isCorrection ? undefined : '0'}
              step="0.01"
              value={hasItems ? vatAmount.toFixed(2) : vat}
              readOnly={hasItems}
              placeholder={round(netAmount * DEFAULT_VAT_RATE).toFixed(2)}
              onChange={(event) => setVat(event.target.value)}
            />
          )}
        </Field>
      </div>

      <Field label={isCorrection ? 'Różnica brutto (±)' : 'Kwota brutto'}>
        {(id) => (
          <Input id={id} value={formatMoney(grossAmount)} readOnly tabIndex={-1} />
        )}
      </Field>

      {!isCorrection && (
        <Field
          label="Pozycje faktury"
          hint={
            hasItems
              ? 'Kwoty netto i VAT wyliczają się z pozycji.'
              : 'Opcjonalne. Bez pozycji faktura rozlicza się kwotą zbiorczą.'
          }
        >
          {() => <LineItemsEditor items={items} onChange={setItems} />}
        </Field>
      )}

      <Field label="Skan faktury" hint="Opcjonalny — z KSeF przychodzą same dane, bez PDF.">
        {(id) => (
          <Input
            id={id}
            type="file"
            accept="application/pdf,image/*"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        )}
      </Field>

      <Field label="Uwagi">
        {(id) => (
          <Textarea
            id={id}
            rows={2}
            value={notes}
            placeholder="np. faktura papierowa od kierowcy"
            onChange={(event) => setNotes(event.target.value)}
          />
        )}
      </Field>

      {validationError && <ErrorBox>{validationError}</ErrorBox>}
      {create.error && (
        <ErrorBox>
          {create.error instanceof ApiError ? create.error.message : 'Nie udało się zapisać.'}
        </ErrorBox>
      )}
    </Modal>
  );
}
